import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import {
  CAMPAIGN_TABLE_FIXED_COLUMN_IDS,
  CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS,
  type CampaignTableColumnId,
} from "@/components/campaign/campaign-table-layout";

const STORAGE_KEY = "adpilot:campaign-table-visible-columns:v2";
const storedColumnsSchema = z.array(z.string());

export function normalizeCampaignTableColumns(values: readonly string[]) {
  const allowed = new Set<string>(CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS);
  return values.filter(
    (value, index): value is (typeof CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS)[number] =>
      allowed.has(value) && values.indexOf(value) === index,
  );
}

function readStoredColumns() {
  if (typeof window === "undefined") return [...CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS];

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored == null) return [...CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS];
    const parsed = storedColumnsSchema.safeParse(JSON.parse(stored));
    return parsed.success
      ? normalizeCampaignTableColumns(parsed.data)
      : [...CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS];
  } catch {
    return [...CAMPAIGN_TABLE_NON_FIXED_COLUMN_IDS];
  }
}

function persistColumns(columns: readonly CampaignTableColumnId[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // Column customization still works for this session when browser storage is unavailable.
  }
}

export function useCampaignTableColumns() {
  const [visibleNonFixedColumns, setVisibleNonFixedColumnsState] = useState(readStoredColumns);

  const setVisibleNonFixedColumns = useCallback((columns: readonly CampaignTableColumnId[]) => {
    const next = normalizeCampaignTableColumns(columns);
    setVisibleNonFixedColumnsState(next);
    persistColumns(next);
  }, []);

  const visibleColumnIds = useMemo(
    () => [...CAMPAIGN_TABLE_FIXED_COLUMN_IDS, ...visibleNonFixedColumns],
    [visibleNonFixedColumns],
  );

  return { visibleColumnIds, visibleNonFixedColumns, setVisibleNonFixedColumns };
}
